import { useState, useEffect, useCallback } from 'react';
import { api } from '@/utils/api';

// ── Plan configuration ────────────────────────────────────────────────────────
// Ordered lowest → highest tier. Add new plans here as they launch.
export const PLAN_TIERS = ['free', 'basic', 'pro', 'enterprise'];

// Fallback limits used only while the API response is still loading.
// Values mirror the backend's free-plan defaults.
const LOADING_DEFAULTS = {
  owners: 50,
  pets_per_owner: 2,
  doctors: 2,
  pet_types: 5,
  admins: 2,
};
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Central hook for subscription data and limit helpers.
 * Any page that needs plan info, usage, or limit checks should use this
 * instead of calling /subscription directly.
 */
export const useSubscription = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    try {
      const response = await api.get('/subscription');
      setSubscription(response.data);
    } catch {
      // Non-critical – pages degrade gracefully to loading defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const plan   = subscription?.plan_type    ?? 'free';
  const limits = subscription?.limits       ?? LOADING_DEFAULTS;
  const usage  = subscription?.current_usage ?? {};

  const isFreePlan = plan === 'free';

  /**
   * Returns true if the current plan is at least as high as `tier`.
   * Example: planAtLeast('basic') → true for basic, pro, enterprise; false for free.
   * Use this instead of direct plan-name comparisons so new tiers work automatically.
   */
  const planAtLeast = (tier) => {
    const currentIdx = PLAN_TIERS.indexOf(plan);
    const targetIdx  = PLAN_TIERS.indexOf(tier);
    return currentIdx !== -1 && targetIdx !== -1 && currentIdx >= targetIdx;
  };

  // ── Limit helpers (always derived from the API response) ─────────────────
  const canAddOwner   = () => (usage.owners    ?? 0) < (limits.owners    ?? LOADING_DEFAULTS.owners);
  const canAddDoctor  = () => (usage.doctors   ?? 0) < (limits.doctors   ?? LOADING_DEFAULTS.doctors);
  const canAddPetType = () => (usage.pet_types ?? 0) < (limits.pet_types ?? LOADING_DEFAULTS.pet_types);

  /** Maximum pets allowed per owner on the current plan */
  const getPetsPerOwnerLimit = () => limits.pets_per_owner ?? LOADING_DEFAULTS.pets_per_owner;

  /** Whether an owner with `petCount` current pets can receive another */
  const canAddPetForOwner = (petCount) => petCount < getPetsPerOwnerLimit();

  /** Maximum admin accounts allowed on the current plan */
  const getAdminLimit = () => limits.admins ?? LOADING_DEFAULTS.admins;

  /** Maximum file attachments per medical record on the current plan */
  const getAttachmentsLimit = () => limits.attachments_per_record ?? 1;

  return {
    subscription,
    loading,
    refresh: fetchSubscription,
    plan,
    limits,
    usage,
    isFreePlan,
    planAtLeast,
    canAddOwner,
    canAddDoctor,
    canAddPetType,
    getPetsPerOwnerLimit,
    canAddPetForOwner,
    getAdminLimit,
    getAttachmentsLimit,
  };
};
